/**
 * Read-only viewer for a walk's published report (owner side).
 * Fetches /user/bookings/:id/walk-report; shows "not published yet" when absent
 * (the backend only returns PUBLISHED reports — DRAFTs 404 and read as null).
 */
import { useEffect } from 'react';
import { Clock, Loader2, PawPrint, Route, X } from 'lucide-react';
import { ImageCarousel } from '@/components/image-carousel';
import { use_owner_walk_report } from '@/features/walk-report/hooks';

function fmt_time(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function WalkReportViewModal({ booking_id, on_close }: { booking_id: string; on_close: () => void }) {
  const report_q = use_owner_walk_report(booking_id);
  const report = report_q.data ?? null;

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);

  const potty =
    report && (report.potty_pee || report.potty_poop)
      ? [report.potty_pee ? 'peed' : null, report.potty_poop ? 'pooped' : null].filter(Boolean).join(' & ')
      : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="walk-report-view-modal"
      onClick={on_close}
    >
      <div className="my-8 w-full max-w-lg rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
            <PawPrint className="h-5 w-5 text-primary" /> Walk report
          </h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted" data-testid="walk-report-view-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {report_q.isLoading ? (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !report ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="walk-report-view-empty">
            Your walker hasn't published a report for this walk yet.
          </p>
        ) : (
          <div className="flex flex-col gap-4 p-5 text-sm">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-muted-foreground" />
                {fmt_time(report.actual_start)} → {fmt_time(report.actual_end)}
              </span>
              {report.distance_km != null && (
                <span className="flex items-center gap-1.5" data-testid="walk-report-distance">
                  <Route className="h-4 w-4 text-muted-foreground" /> {report.distance_km} km
                </span>
              )}
            </div>

            {potty && (
              <p className="text-sm">
                <span className="font-semibold text-muted-foreground">Potty:</span> {potty}
              </p>
            )}

            {report.notes && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p>
                <p className="whitespace-pre-wrap">{report.notes}</p>
              </div>
            )}

            {report.photos.length > 0 && (
              <div className="flex flex-col gap-1.5" data-testid="walk-report-photos">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Photos</p>
                <ImageCarousel images={report.photos} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
